import {
  effectiveResolutionThresholdPpbFromPolicy,
  RESOLUTION_THRESHOLD_PPB_ONE,
  type ResolutionThresholdDecayPolicy,
  resolutionThresholdFloorPpb,
} from '@morfi/contract-sdk/conviction';
import type { MarketConfig } from '../core/internal/market-config-catalog.js';

export type MarketResolutionThresholdUnavailableReason =
  | 'config-unavailable'
  | 'invalid-config'
  | 'invalid-outcome-count'
  | 'missing-trading-start'
  | 'invalid-trading-start'
  | 'missing-resolution-time'
  | 'invalid-resolution-time';

export type MarketResolutionThresholdPolicy =
  | { readonly kind: 'ready'; readonly policy: ResolutionThresholdConfigPolicy }
  | { readonly kind: 'unavailable'; readonly reason: MarketResolutionThresholdUnavailableReason };

export type MarketResolutionThresholdState =
  | {
      readonly kind: 'ready';
      readonly thresholdPpb: bigint;
      readonly thresholdFraction: number;
      readonly thresholdPercent: number;
      readonly nextDropAtMs: number | null;
      readonly frozen: boolean;
    }
  | { readonly kind: 'unavailable'; readonly reason: MarketResolutionThresholdUnavailableReason };

type UnavailableThresholdState = Extract<
  MarketResolutionThresholdState,
  { readonly kind: 'unavailable' }
>;
type ParsedTimestamp = { readonly kind: 'timestamp'; readonly unixSeconds: bigint };

export interface DeriveMarketResolutionThresholdPolicyInput {
  readonly config: MarketConfig | undefined;
  readonly outcomeCount: number;
}

export interface ResolutionThresholdStateAtInput {
  readonly policy: MarketResolutionThresholdPolicy;
  readonly status: string;
  readonly tradingStartedAt: string | null;
  readonly resolvedAt: string | null;
  readonly nowTs: bigint;
}

export interface ResolutionThresholdConfigPolicy {
  readonly initialThresholdPpb: bigint;
  readonly thresholdFloorPpb: bigint;
  readonly decayDelaySecs: bigint;
  readonly dropPpb: bigint;
  readonly dropIntervalSecs: bigint;
}

export type MarketResolutionThresholdContractPolicy =
  | { readonly kind: 'ready'; readonly policy: ResolutionThresholdDecayPolicy }
  | UnavailableThresholdState;

export function deriveMarketResolutionThresholdPolicy(
  input: DeriveMarketResolutionThresholdPolicyInput,
): MarketResolutionThresholdPolicy {
  if (input.config === undefined) return unavailable('config-unavailable');
  if (
    !Number.isSafeInteger(input.outcomeCount) ||
    input.outcomeCount < 2 ||
    input.outcomeCount > 10
  )
    return unavailable('invalid-outcome-count');

  try {
    const outcomeCount = BigInt(input.outcomeCount);
    const initialThresholdPpb = requiredConfigU64(input.config.convictionResolutionThresholdPpb);
    const floorMarginPpb = requiredConfigU64(input.config.resolutionThresholdFloorMarginPpb);
    const decayDelaySecs = requiredConfigU64(input.config.resolutionThresholdDecayDelaySecs);
    const dropIntervalSecs = requiredConfigU64(input.config.resolutionThresholdDropIntervalSecs);
    const dropPpb = requiredConfigU64(input.config.resolutionThresholdDropPpb);
    const thresholdFloorPpb = resolutionThresholdFloorPpb(outcomeCount, floorMarginPpb);
    if (
      thresholdFloorPpb > initialThresholdPpb ||
      initialThresholdPpb > RESOLUTION_THRESHOLD_PPB_ONE
    )
      return unavailable('invalid-config');
    const policy = {
      initialThresholdPpb,
      thresholdFloorPpb,
      decayDelaySecs,
      dropPpb,
      dropIntervalSecs,
    };
    effectiveResolutionThresholdPpbFromPolicy(toContractPolicy(policy, 0n), 0n);
    return {
      kind: 'ready',
      policy,
    };
  } catch {
    return unavailable('invalid-config');
  }
}

export function resolutionThresholdStateAt(
  input: ResolutionThresholdStateAtInput,
): MarketResolutionThresholdState {
  if (input.policy.kind === 'unavailable') return input.policy;
  if (input.status === 'created' || input.status === 'baking') {
    return readyState(input.policy.policy.initialThresholdPpb, null, false);
  }

  const contractPolicy = toMarketResolutionThresholdContractPolicy(
    input.policy,
    input.tradingStartedAt,
  );
  if (contractPolicy.kind === 'unavailable') return contractPolicy;
  const evaluationTs = resolutionEvaluationTime(input.status, input.resolvedAt, input.nowTs);
  if (evaluationTs.kind === 'unavailable') return evaluationTs;

  try {
    const thresholdPpb = effectiveResolutionThresholdPpbFromPolicy(
      contractPolicy.policy,
      evaluationTs.unixSeconds,
    );
    return readyState(
      thresholdPpb,
      thresholdPpb === contractPolicy.policy.thresholdFloorPpb || evaluationTs.frozen
        ? null
        : nextDropAtMs(contractPolicy.policy, evaluationTs.unixSeconds),
      evaluationTs.frozen,
    );
  } catch {
    return unavailable('invalid-config');
  }
}

export function toMarketResolutionThresholdContractPolicy(
  policy: MarketResolutionThresholdPolicy,
  tradingStartedAt: string | null,
): MarketResolutionThresholdContractPolicy {
  if (policy.kind === 'unavailable') return policy;
  const tradingStartedTs = timestampToUnixSeconds(tradingStartedAt, 'trading');
  if (tradingStartedTs.kind === 'unavailable') return tradingStartedTs;
  return { kind: 'ready', policy: toContractPolicy(policy.policy, tradingStartedTs.unixSeconds) };
}

function requiredConfigU64(value: string | undefined): bigint {
  if (typeof value !== 'string' || !/^(0|[1-9]\d*)$/.test(value)) {
    throw new Error('invalid config');
  }
  const parsed = BigInt(value);
  if (parsed > 18446744073709551615n) throw new Error('invalid config');
  return parsed;
}

function timestampToUnixSeconds(
  timestamp: string | null,
  kind: 'trading' | 'resolution',
): ParsedTimestamp | UnavailableThresholdState {
  if (timestamp === null) {
    return unavailable(kind === 'trading' ? 'missing-trading-start' : 'missing-resolution-time');
  }
  const milliseconds = Date.parse(timestamp);
  if (!Number.isFinite(milliseconds)) {
    return unavailable(kind === 'trading' ? 'invalid-trading-start' : 'invalid-resolution-time');
  }
  return { kind: 'timestamp', unixSeconds: BigInt(Math.floor(milliseconds / 1000)) };
}

function resolutionEvaluationTime(
  status: string,
  resolvedAt: string | null,
  nowTs: bigint,
):
  | { readonly kind: 'timestamp'; readonly unixSeconds: bigint; readonly frozen: boolean }
  | UnavailableThresholdState {
  if (status !== 'resolved' && status !== 'graduated') {
    return { kind: 'timestamp', unixSeconds: nowTs, frozen: false };
  }
  const resolvedTs = timestampToUnixSeconds(resolvedAt, 'resolution');
  if (resolvedTs.kind === 'unavailable') return resolvedTs;
  return { ...resolvedTs, frozen: true };
}

function nextDropAtMs(policy: ResolutionThresholdDecayPolicy, nowTs: bigint): number {
  const elapsedAfterDelay = nowTs - policy.tradingStartedTs - policy.decayDelaySecs;
  const appliedDrops = elapsedAfterDelay > 0n ? elapsedAfterDelay / policy.dropIntervalSecs : 0n;
  return Number(
    (policy.tradingStartedTs +
      policy.decayDelaySecs +
      (appliedDrops + 1n) * policy.dropIntervalSecs) *
      1000n,
  );
}

function toContractPolicy(
  policy: ResolutionThresholdConfigPolicy,
  tradingStartedTs: bigint,
): ResolutionThresholdDecayPolicy {
  return { ...policy, tradingStartedTs };
}

function readyState(
  thresholdPpb: bigint,
  nextDropAtMs: number | null,
  frozen: boolean,
): MarketResolutionThresholdState {
  const thresholdFraction = Number(thresholdPpb) / Number(RESOLUTION_THRESHOLD_PPB_ONE);
  return {
    kind: 'ready',
    thresholdPpb,
    thresholdFraction,
    thresholdPercent: thresholdFraction * 100,
    nextDropAtMs,
    frozen,
  };
}

function unavailable(
  reason: MarketResolutionThresholdUnavailableReason,
): UnavailableThresholdState {
  return { kind: 'unavailable', reason };
}
